// src/com/file_of_report/TransPlanReportInput.js
import React, { useEffect, useRef, useState } from 'react';
import {
    Button, DialogContent, DialogActions, CircularProgress, Grid, Box,
    FormControl, FormLabel, RadioGroup, FormControlLabel, Radio
} from '@mui/material';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import CloseIcon from '@mui/icons-material/Close';
import { AppWord, AppObject, AppStyle, TopCode } from '../../value/AppConst';
import { useLazyQuery, useMutation } from '@apollo/client';
import { useAlertSetter } from '../../api/app-hook';
import DraggableEditor from '../../common/DraggableEditor';
import InputUtil from '../../value/InputUtil';
import ValueMisc from '../../value/ValueMisc';
import { GET_FILE_CREATE_HIS } from '../file_create_his/FileCreateHisGql';
import { TransPlanReportDict } from './ReportDict';
import { CREATE_TRANS_PLAN_REPORT } from './TransPlanReportGql';
import Datetime24Picker from '../../common/Datetime24Picker';

const MAX_ATTEMPTS = 10; // 레포트 생성 확인을 위한 조회 최대 시도 횟수
const POLLING_INTERVAL = 3000; // 3초 간격으로 레포트 생성 확인 조회

const InputMap = TransPlanReportDict;

const initialData = {...AppObject.emptyInputData,};
initialData.data.timeFrom = ValueMisc.getYMD(new Date()) + ' 00:00:00';
initialData.data.timeTill = ValueMisc.getYMD(new Date()) + ' 23:00:00';

/**
 * 운송요청 리포트 생성 Input Dialog
 * @param {*} param0 
 */
export default function TransPlanReportInput({
    open,
    onClose,
    onComplete,
}) {
    const [attemtCount, setAttemptCount] = useState(0);
    const [message, setMessage] = useState('');
    const [input, setInput] = useState({...initialData});
    const [tommorow, setTommorow] = useState('');
    const addAlert = useAlertSetter();
    const attemptCountRef = useRef(0);
    const timeoutRef = useRef(null);

    const [createReport, responseCreateReport] = useMutation( CREATE_TRANS_PLAN_REPORT, {
        onCompleted: (data, option) => onCompleteCreateReport(data, option),
        onError: (error) => {
            addAlert({title: '운송요청 리포트 생성 오류', message: [error.message]})
        }
    });
        // 레포트 생성 후 일정 시간 간격으로 조회해서 생성 완료 여부 확인.
    const [getFileCreateHisItem, responseGetItem] = useLazyQuery(GET_FILE_CREATE_HIS, {
            ...AppObject.NoCachedFetch,
            onCompleted: (data, option) => {onCompleteGetItem(data, option)},
            onError: (error) => addAlert({title: "레포트 생성 확인 오류", message: [error.message]})
    });
    
    useEffect(() => {
        if(open) {
            const d = new Date();
            d.setDate(d.getDate() + 1);
            setTommorow(ValueMisc.getYMD(d));
            setInput({...initialData});
            setMessage('레포트 생성을 위한 기초정보를 설정 후 생성 버튼을 눌러주세요.');
        }
        else {
            setInput({...initialData});
            setMessage('');
        }
    }, [open]);

    const repeatCheckReportCreation = (fileCreateHisSeq) => {
        if(attemptCountRef.current >= MAX_ATTEMPTS) {
            addAlert({
                title: "레포트 생성 확인 실패",
                message: ['레포트 생성 확인을 반복했으나 아직 확인할 수 없습니다. 잠시 후 목록을 다시 조회해서 확인해 주세요.']});
            return;
        }

        attemptCountRef.current += 1;
        setAttemptCount(attemptCountRef.current);
        getFileCreateHisItem({variables:{fileCreateHisSeq:fileCreateHisSeq}});
    };

    const onCompleteGetItem = (data, clientOption) => {
        if(data.fileCreateHisItem) {
            // Check fileDownUp==='DOWN' or try again after some delay
            if(data.fileCreateHisItem.fileDownUp === 'DOWN') {
                // 레포트 생성 완료
                setAttemptCount(0);
                onComplete();
            }
            else if(data.fileCreateHisItem.fileDownUp === 'UP') {
                setMessage(`레포트 생성 중입니다. ${POLLING_INTERVAL/1000}초 후 다시 확인합니다. (${MAX_ATTEMPTS - attemptCountRef.current}회)`);
                timeoutRef.current = setTimeout(() => {
                    repeatCheckReportCreation(data.fileCreateHisItem.fileCreateHisSeq);
                }, POLLING_INTERVAL);
            }
            else {
                // 기타 상태 - 오류로 간주 (FAILED)
                setAttemptCount(0);
                addAlert({
                    title: "레포트 생성 오류",
                    message: ['레포트 생성 중 알 수 없는 상태가 발생했습니다. 관리자에게 문의해 주세요.']});
            }
        }
        else {
            addAlert({
                title: "레포트 생성 확인 오류",
                message: ['레포트 생성 내역을 확인하는 중 알 수 없는 오류가 발생했습니다. 관리자에게 문의해 주세요.']});
        }
    };

    const onCompleteCreateReport = (data, clientOption) => {
        if(data.transPlanReportAdd.ok) {
            setMessage('리포트 생성 요청이 정상적으로 접수되었습니다. 결과를 확인 중입니다.');
            attemptCountRef.current = 0;
            repeatCheckReportCreation(data.transPlanReportAdd.item.fileCreateHisSeq);
        }
        else {
            setMessage('리포트 생성 오류가 발생했습니다.');
            addAlert({title: '운송요청 리포트 생성 오류', message: [data.transPlanReportAdd.message]});
        }
    };
        
    const onChangeValue = (id, value, error) => {
        const newInput = InputUtil.reflectData(input, id, value, error, InputMap, false);
        if(id === InputMap.timeFrom.id && input.data.timeTill < value) {
            newInput.data.timeTill = value;
        }
        else if(id === InputMap.timeTill.id && input.data.timeFrom > value) {
            newInput.data.timeFrom = value;
        }
        setInput(newInput);
    };

    const onClickCreate = () => {
        const param = {
            timeFrom: input.data.timeFrom,
            timeTill: input.data.timeTill,
        };
        if(input.data.reqStateFlag===TopCode.ReqStateFlag.TsrIssued || input.data.reqStateFlag===TopCode.ReqStateFlag.TsrNotIssued) {
            param.reqStateFlag = input.data.reqStateFlag;
        }
        setMessage('리포트 생성을 요청하는 중입니다.');
        createReport({variables: {transPlanReport: param}});
    };

    const loadingAny = responseCreateReport.loading || responseGetItem.loading;

    return(
        <DraggableEditor open={open} maxWidth='sm'
            startIcon={<NewspaperIcon fontSize={AppWord.LARGE} style={{color:'white', marginRight:15}} />}
            title={"운송요청 파일 생성"}
        >
            <DialogContent>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Datetime24Picker
                            dict={InputMap.timeFrom} value={input.data.timeFrom}
                            onChange={onChangeValue}
                            maxDate={tommorow}
                        />
                        부터
                        <Datetime24Picker
                            dict={InputMap.timeTill} value={input.data.timeTill}
                            onChange={onChangeValue}
                            maxDate={tommorow}
                        />
                        까지
                    </Grid>
                    <Grid item xs={12}>
                        <FormControl>
                            <FormLabel id={InputMap.reqStateFlag.id + '-label'} style={{marginBottom:5}}>TSR 발행 여부</FormLabel>
                            <RadioGroup
                                row
                                aria-labelledby={InputMap.reqStateFlag.id + '-label'}
                                name={InputMap.reqStateFlag.id}
                                value={input.data.reqStateFlag || 'x'}
                                onChange={(event) => onChangeValue(InputMap.reqStateFlag.id, event.target.value, false)}
                            >
                                <FormControlLabel
                                    value="x"
                                    control={<Radio />}
                                    label="전체"
                                />
                                <FormControlLabel
                                    value={TopCode.ReqStateFlag.TsrNotIssued}
                                    control={<Radio />}
                                    label="미발행"
                                />
                                <FormControlLabel
                                    value={TopCode.ReqStateFlag.TsrIssued}
                                    control={<Radio />}
                                    label="발행"
                                />
                            </RadioGroup>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Box
                            style={{
                                fontSize: '0.9rem',
                                border: '1px solid #ccc',
                                padding: 12,
                                borderRadius: 4,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center'
                            }}
                        >
                            {
                                attemtCount > 0 &&
                                <CircularProgress size={20} style={{marginRight:10}} />
                            }
                            {message}
                        </Box>
                    </Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                {
                    loadingAny &&
                    <CircularProgress size={24} style={{marginRight:15}} />
                }
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.primary}
                    startIcon={<NoteAddIcon />}
                    onClick={onClickCreate}
                    disabled={loadingAny}
                >생성</Button>
                <Button
                    variant={AppStyle.mui.contained}
                    color={AppStyle.mui.warning}
                    startIcon={<CloseIcon />}
                    onClick={onClose}
                >취소</Button>
            </DialogActions>
        </DraggableEditor>
    );
}
